import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Check, Pencil, X } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const FIELDS = ["title", "date", "location", "description"];

export const EventsTab = () => {
  const [events, setEvents] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/events");
    setEvents(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (ev) => {
    setEditing(ev.id);
    setDraft({ title: ev.title || "", date: ev.date || "", location: ev.location || "", description: ev.description || "" });
  };

  const saveEdit = async (id) => {
    await adminApi.put(`/admin/events/${id}`, draft);
    setEditing(null);
    load();
  };

  const review = async (id, action) => {
    await adminApi.post(`/admin/events/${id}/status`, { action });
    load();
  };

  if (!events) return <p className="text-sm text-cream/50">Loading events…</p>;

  return (
    <div data-testid="events-tab">
      {events.length === 0 ? (
        <div className="rounded-3xl border border-cream/10 px-6 py-12 text-center" data-testid="events-empty">
          <CalendarCheck className="mx-auto h-8 w-8 text-gold/50" />
          <p className="mt-4 font-serif text-2xl text-cream">No events submitted yet.</p>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-cream/50">
            When members submit local LGBTQ+ meetups in the app, they'll queue here for your review — edit details, then approve or reject.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => (
            <div key={ev.id} className="rounded-3xl border border-cream/10 p-6" data-testid={`event-row-${ev.id}`}>
              {editing === ev.id ? (
                <div className="space-y-3">
                  {FIELDS.map((f) => (
                    <Input
                      key={f}
                      value={draft[f]}
                      onChange={(e) => setDraft({ ...draft, [f]: e.target.value })}
                      placeholder={f[0].toUpperCase() + f.slice(1)}
                      data-testid={`event-edit-${f}-${ev.id}`}
                      className="h-11 rounded-full border-cream/20 bg-cream/5 px-5 text-sm text-cream placeholder:text-cream/40"
                    />
                  ))}
                  <div className="flex gap-2">
                    <Button onClick={() => saveEdit(ev.id)} data-testid={`event-save-${ev.id}`} className="h-10 rounded-full bg-gold px-6 text-xs font-bold uppercase tracking-[0.12em] text-ink hover:bg-gold-deep">Save</Button>
                    <Button onClick={() => setEditing(null)} variant="ghost" data-testid={`event-cancel-${ev.id}`} className="h-10 rounded-full px-6 text-xs font-bold uppercase tracking-[0.12em] text-cream/60">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-cream/85">{ev.title || "Untitled event"}</p>
                    <p className="mt-1 text-sm text-cream/55">{[ev.date, ev.location].filter(Boolean).join(" · ")}</p>
                    {ev.description && <p className="mt-2 max-w-xl text-sm leading-relaxed text-cream/55">{ev.description}</p>}
                    <p className="mt-2 text-xs uppercase tracking-[0.15em] text-cream/35">{ev.status || "pending"}</p>
                  </div>
                  <div className="flex gap-2">
                    {(ev.status || "pending") === "pending" && (
                      <>
                        <button onClick={() => startEdit(ev)} data-testid={`event-edit-btn-${ev.id}`} className="flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-cream/70 transition-colors hover:border-gold hover:text-gold">
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => review(ev.id, "approve")} data-testid={`event-approve-${ev.id}`} className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-gold transition-colors hover:bg-gold hover:text-ink">
                          <Check className="h-3 w-3" /> Approve
                        </button>
                        <button onClick={() => review(ev.id, "reject")} data-testid={`event-reject-${ev.id}`} className="flex items-center gap-1.5 rounded-full border border-red-500/40 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-red-400 transition-colors hover:bg-red-500 hover:text-cream">
                          <X className="h-3 w-3" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
